/**
 * Academic Neobrutalism Ultra-Fast Top Progress Bar
 * Provides instant (0ms) visual confirmation for navigation, clicks, and network requests.
 * Aligned with ckw-design principles: tactile, immediate responsiveness, high contrast.
 */

let barElement = null;
let headElement = null;
let currentPercent = 0;
let progressTimer = null;
let autoFinishTimer = null;
let isRunning = false;
let activeRequests = 0;

function ensureElements() {
  if (typeof document === 'undefined') return false;
  if (!barElement || !document.body.contains(barElement)) {
    const existing = document.getElementById('nbTopProgressBar');
    if (existing) {
      barElement = existing;
      headElement = existing.querySelector('.nb-top-progress-head');
      return true;
    }

    barElement = document.createElement('div');
    barElement.id = 'nbTopProgressBar';
    barElement.className = 'nb-top-progress-bar';
    barElement.setAttribute('role', 'progressbar');
    barElement.setAttribute('aria-valuemin', '0');
    barElement.setAttribute('aria-valuemax', '100');
    barElement.setAttribute('aria-valuenow', '0');

    headElement = document.createElement('div');
    headElement.className = 'nb-top-progress-head';
    barElement.appendChild(headElement);

    document.body.prepend(barElement);
  }
  return true;
}

export const topProgress = {
  start() {
    if (typeof window === 'undefined') return;
    if (!ensureElements()) return;

    activeRequests++;

    // Clear any prior autoFinish failsafe
    if (autoFinishTimer) {
      clearTimeout(autoFinishTimer);
      autoFinishTimer = null;
    }

    // Auto-finish failsafe: if finish() is not called within 4 seconds,
    // guarantee the progress bar completes to 100% and gracefully vanishes
    autoFinishTimer = setTimeout(() => {
      if (isRunning) {
        topProgress.finish(true);
      }
    }, 4000);

    if (isRunning) {
      // If already running, give a subtle forward bump
      if (currentPercent < 80) {
        currentPercent = Math.min(85, currentPercent + 8);
        if (barElement) {
          barElement.style.width = `${currentPercent}%`;
          barElement.setAttribute('aria-valuenow', String(Math.round(currentPercent)));
        }
      }
      return;
    }

    isRunning = true;
    if (progressTimer) clearInterval(progressTimer);

    // Instantaneous (0ms) jump to 30%
    currentPercent = 30;
    barElement.style.opacity = '1';
    barElement.style.display = 'block';
    barElement.style.width = `${currentPercent}%`;
    barElement.setAttribute('aria-valuenow', String(currentPercent));

    // Smooth incremental crawl
    progressTimer = setInterval(() => {
      if (currentPercent < 60) {
        currentPercent += Math.random() * 8 + 4;
      } else if (currentPercent < 85) {
        currentPercent += Math.random() * 3 + 1;
      } else if (currentPercent < 95) {
        currentPercent += 0.5;
      }
      if (barElement) {
        barElement.style.width = `${currentPercent}%`;
        barElement.setAttribute('aria-valuenow', String(Math.round(currentPercent)));
      }
    }, 120);
  },

  set(percent) {
    if (!ensureElements()) return;
    currentPercent = Math.min(100, Math.max(0, percent));
    barElement.style.width = `${currentPercent}%`;
    barElement.setAttribute('aria-valuenow', String(Math.round(currentPercent)));
  },

  finish(force = false) {
    if (typeof window === 'undefined') return;
    if (activeRequests > 0 && !force) {
      activeRequests--;
      if (activeRequests > 0) return;
    }
    activeRequests = 0;

    if (autoFinishTimer) {
      clearTimeout(autoFinishTimer);
      autoFinishTimer = null;
    }

    if (progressTimer) {
      clearInterval(progressTimer);
      progressTimer = null;
    }

    if (!ensureElements()) {
      isRunning = false;
      return;
    }

    // Snap to 100% full immediately
    currentPercent = 100;
    barElement.style.width = '100%';
    barElement.setAttribute('aria-valuenow', '100');

    // Smooth fade out after arriving at 100%
    setTimeout(() => {
      if (barElement) {
        barElement.style.opacity = '0';
        setTimeout(() => {
          if (barElement && currentPercent === 100) {
            barElement.style.width = '0%';
            barElement.style.display = 'none';
            currentPercent = 0;
            isRunning = false;
          }
        }, 200);
      } else {
        isRunning = false;
      }
    }, 150);
  },

  fail() {
    if (!ensureElements()) return;
    if (barElement) {
      barElement.classList.add('is-failed');
    }
    this.finish(true);
    setTimeout(() => {
      if (barElement) barElement.classList.remove('is-failed');
    }, 500);
  }
};
